/**
 * Admin SMS Responses Repository
 *
 * Manages inbound SMS reply tracking and outbound SMS alerts for the admin dashboard.
 */

import { createRepository } from 'data/utils/createRepository';
import { getApiUrl } from 'data/services/apiConstants';
import { get } from 'data/services/apiService';

const buildQuery = (params = {}) => {
	const searchParams = new URLSearchParams();
	Object.entries(params).forEach(([key, val]) => {
		if (val !== undefined && val !== null && val !== '') {
			searchParams.append(key, val);
		}
	});
	const queryString = searchParams.toString();
	return queryString ? `?${queryString}` : '';
};

const adminSmsResponsesRepository = createRepository({
	endpoint: 'admin/sms-responses',
	name: 'adminSmsResponses',
	statePath: 'adminSmsResponses',

	customMethods: {
		/**
		 * Get inbound SMS statistics
		 * Usage: actions.getStatistics({ days: 7 })
		 */
		getStatistics: ({ days = 7, authToken, dispatch }) => {
			return get({
				url: `${getApiUrl()}/api/admin/sms-responses/statistics?days=${days}`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Get inbound filter options
		 * Usage: actions.getFilterOptions()
		 */
		getFilterOptions: ({ authToken, dispatch }) => {
			return get({
				url: `${getApiUrl()}/api/admin/sms-responses/filter-options`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Get paginated outbound SMS list
		 * Usage: actions.getOutboundList({ page: 1, per_page: 25, status: 'sent', ... })
		 */
		getOutboundList: ({ authToken, dispatch, ...params }) => {
			return get({
				url: `${getApiUrl()}/api/admin/sms-responses/outbound${buildQuery(params)}`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Get outbound SMS statistics
		 * Usage: actions.getOutboundStatistics({ days: 7 })
		 */
		getOutboundStatistics: ({ days = 7, authToken, dispatch }) => {
			return get({
				url: `${getApiUrl()}/api/admin/sms-responses/outbound/statistics?days=${days}`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Get outbound filter options
		 * Usage: actions.getOutboundFilterOptions()
		 */
		getOutboundFilterOptions: ({ authToken, dispatch }) => {
			return get({
				url: `${getApiUrl()}/api/admin/sms-responses/outbound/filter-options`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Fetch single outbound SMS details
		 * Usage: actions.fetchOutboundOne({ id: 123 })
		 */
		fetchOutboundOne: ({ id, authToken, dispatch }) => {
			return get({
				url: `${getApiUrl()}/api/admin/sms-responses/outbound/${id}`,
				authToken,
				dispatch,
			});
		},
	},
});

export const {
	name,
	statePath,
	thunks: adminSmsResponsesThunks,
	reducer: adminSmsResponsesReducer,
	actions: adminSmsResponsesActions,
	types: adminSmsResponsesTypes,
	selectors: adminSmsResponsesSelectors,
	useHook: useAdminSmsResponses,
} = adminSmsResponsesRepository;

export default adminSmsResponsesRepository;
